// What a survey-end page may show, decided ONLY from the backend's answer to
// GetUserOneAssignedSurveyCallback — never from the URL path. The path says
// what the survey provider claims happened; only the backend knows whether
// it was recorded.
//
//   redirect  go to `url` (partner passback, country thank-you page, or the
//             partner hold page for a closed survey)
//   thanks    show the thank-you page; `status` is the backend status to
//             display ('Completed', 'Terminated', ...): the SAVED status
//             (`finalStatus`), never the one in the URL. A Complete saved as
//             Quality Terminated (IP changed, speeder, Verisoul) shows the QT
//             page. `statusReason` says why; it is never shown.
//   invalid   "This link is not valid or has expired." — nothing else
//
// Rules:
//   recorded === true                     -> redirect if a URL came back, else thanks (saved status)
//   reason already_recorded|already_final -> redirect if a URL came back, else thanks (stored status)
//   recorded === false + partner hold URL -> redirect (closed-survey partner)
//   anything else, or no/odd response     -> invalid

export type CallbackOutcome =
    | { kind: 'redirect'; url: string }
    | { kind: 'thanks'; status: string }
    | { kind: 'invalid'; reason: string };

const REPEAT_REASONS = ['already_recorded', 'already_final'];
const HOLD_PAGE = '/#/partner-survey-hold';

export function decideCallbackOutcome(result: any, requestedStatus: string): CallbackOutcome {
    if (!result || typeof result !== 'object') {
        return { kind: 'invalid', reason: 'no_response' };
    }
    const url: string = String(result.redirectUrl || result.url || '');
    const reason: string = String(result.reason || '');
    const finalStatus: string = String(result.finalStatus || '');

    if (result.recorded === true) {
        // requestedStatus only for a backend that predates finalStatus.
        return url ? { kind: 'redirect', url } : { kind: 'thanks', status: finalStatus || requestedStatus };
    }
    if (result.recorded === false && REPEAT_REASONS.includes(reason)) {
        if (url) return { kind: 'redirect', url };
        const stored = String(finalStatus || result.recordedStatus || result.status || '');
        return stored ? { kind: 'thanks', status: stored } : { kind: 'invalid', reason };
    }
    if (result.recorded === false && url.includes(HOLD_PAGE)) {
        return { kind: 'redirect', url };
    }
    return { kind: 'invalid', reason: reason || 'unrecognised_response' };
}

export const INVALID_LINK_MESSAGE = 'This link is not valid or has expired.';

// A userid the survey provider never substituted ("{userid}", "[USER_ID]",
// "%userid%", "<userid>", literally URL-encoded braces). Same pattern as the
// backend's utils/ipFallback.js.
export const USERID_PLACEHOLDER = /^(\{+\s*user[_-]?id\s*\}+|\[+\s*user[_-]?id\s*\]+|%+\s*user[_-]?id\s*%+|<+\s*user[_-]?id\s*>+|%7b+\s*user[_-]?id\s*%7d+|%5b+\s*user[_-]?id\s*%5d+|%3c+\s*user[_-]?id\s*%3e+)$/i;

// Same pattern as the backend's isUuid in GetUserOneAssignedSurveyCallback.
export const isUuid = (s: unknown): s is string =>
    typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);

// The raw userid a callback link carries. Recontact unique links put the old
// respondent id in `ids=` (the client returns it as userid=) and our id in
// `newids={userid}`: a UUID newids is ours, so it wins. Without one (absent,
// or the client never substituted it) the userid is used, as before.
export function linkUserId(params: URLSearchParams): string | null {
    const newIds = (params.get('newids') || '').trim();
    return isUuid(newIds) ? newIds : params.get('userid');
}

// Universal returns A1: the end link's whole query goes to the backend too,
// so it can find our id under any parameter name (pid, RID, trans_id, ...).
// Values are clipped; the link is the client's, not ours.
export function endLinkQuery(params: URLSearchParams): Record<string, string> {
    const out: Record<string, string> = {};
    let n = 0;
    params.forEach((value, key) => {
        if (n >= 60 || Object.prototype.hasOwnProperty.call(out, key)) return;
        out[key.slice(0, 64)] = String(value).slice(0, 2000);
        n += 1;
    });
    return out;
}

// SCRUM-130: the backend's GET /r end link recorded the hit itself and sent
// the respondent to the thank-you page with dxview=1 and the stored status in
// the path. That page displays it and never posts.
export const isServerRecordedView = (params: URLSearchParams): boolean => params.get('dxview') === '1';

// The userid part of the callback POST. A missing or placeholder userid is
// still sent, as userid '' with placeholderUserId=true: the backend then
// identifies the respondent by the entry from this IP (or rejects the hit,
// and the page shows what it answered).
export function callbackUserId(raw: string | null | undefined): { userId: string; placeholderUserId?: true } {
    const userId = (raw || '').trim();
    if (!userId || USERID_PLACEHOLDER.test(userId)) return { userId: '', placeholderUserId: true };
    return { userId };
}

// Display metadata per backend status. 'Verisoul Blocked' is a quality
// termination to the respondent.
export const STATUS_DISPLAY: Record<string, { title: string; bg: string; fg: string; thanks: string }> = {
    'Completed':          { title: 'Completed',          bg: '#28a745', fg: '#ffffff', thanks: 'You have completed the survey. Thank you for taking part.' },
    'Terminated':         { title: 'Terminated',         bg: '#dc3545', fg: '#ffffff', thanks: 'Unfortunately you did not qualify for this survey. Thank you for your time.' },
    'Over Quota':         { title: 'Over Quota',         bg: '#ffc107', fg: '#212529', thanks: 'This survey has already received all the responses it needs for your group. Thank you for your time.' },
    'Quality Terminated': { title: 'Quality Terminated', bg: '#6c757d', fg: '#ffffff', thanks: 'Your response could not be accepted for this survey. Thank you for your time.' },
};
export const displayFor = (status: string) =>
    STATUS_DISPLAY[status === 'Verisoul Blocked' ? 'Quality Terminated' : status] || null;
